import React, { useState, useEffect, useCallback } from "react";
import ChatPanel from "./components/ChatPanel";
import NotificationPanel from "./components/NotificationPanel";
import PropertiesPanel from "./components/PropertiesPanel";
import BannerPanel from "./components/BannerPanel";
import ReviewsPanel from "./components/ReviewsPanel";
import ReportsPanel from "./components/ReportsPanel";
import ReelsPanel from "./components/ReelsPanel";
import DealersPanel from "./components/DealersPanel";
import FacilitiesPanel from "./components/FacilitiesPanel";
import UsersPanel from "./components/UsersPanel";
import SettingsPanel from "./components/SettingsPanel";
import AdminOverview from "./components/AdminOverview";
import Login, { isTeamSignInPath } from "./components/Login";

// Dealer Panels
import DealerDashboard from "./components/DealerDashboard";
import DealerListings from "./components/DealerListings";
import DealerLeads from "./components/DealerLeads";
import DealerChat from "./components/DealerChat";
import DealerStaff from "./components/DealerStaff";
import DealerProfile from "./components/DealerProfile";
import DealerReels from "./components/DealerReels";
import DealerOffices from "./components/DealerOffices";
import DealerNotifications from "./components/DealerNotifications";

import { auth, db } from "./firebase";
import { migrateLegacyOffices } from "./utils/officeUtils";
import { readCachedBrand, cacheBrand, applyTabBrand } from "./utils/brand";
import { takePhoneSignIn } from "./utils/phoneSignIn";
import { useThemeMode } from "./hooks/useThemeMode";
import { useWideLogo } from "./hooks/useWideLogo";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc, collection, query, where, onSnapshot } from "firebase/firestore";
import {
  Message, Home, People, Profile, Gallery, Notification,
  Star1, Setting4, ArrowLeft2, ArrowRight2, Category, More, Flag, VideoPlay,
  Building, Moon, Sun1, LogoutCurve,
} from "iconsax-reactjs";
import "./App.css";

// Admin sections, grouped in the rail. Order within a group is rail order.
const NAV_ITEMS = [
  { id: "overview", group: "", label: "Dashboard", desc: "The marketplace at a glance", icon: <Category size={20} variant="Linear" /> },
  { id: "chat", group: "Support", label: "Customer Support", shortLabel: "Support", desc: "Conversations with app users", icon: <Message size={20} variant="Linear" /> },
  { id: "properties", group: "Marketplace", label: "Properties", desc: "Every listing on GharSetu", icon: <Home size={20} variant="Linear" /> },
  { id: "reels", group: "Marketplace", label: "Reels", desc: "Moderate property reels", icon: <VideoPlay size={20} variant="Linear" /> },
  { id: "reviews", group: "Marketplace", label: "Reviews", desc: "Approve user reviews", icon: <Star1 size={20} variant="Linear" /> },
  { id: "reports", group: "Marketplace", label: "Reports", desc: "Triage abuse reports", icon: <Flag size={20} variant="Linear" /> },
  { id: "dealers", group: "People", label: "Dealers", desc: "Dealer accounts and access", icon: <People size={20} variant="Linear" /> },
  { id: "users", group: "People", label: "Users", desc: "App users and notifications", icon: <Profile size={20} variant="Linear" /> },
  { id: "banner", group: "Content", label: "Banners", desc: "Home screen banners", icon: <Gallery size={20} variant="Linear" /> },
  { id: "notifications", group: "Content", label: "Notifications", desc: "Send push notifications", icon: <Notification size={20} variant="Linear" /> },
  { id: "facilities", group: "Content", label: "Facilities", desc: "Amenities shown on listings", icon: <Setting4 size={20} variant="Linear" /> },
];

// Settings/Profile lives in the rail footer, not the grouped list, but the
// mobile "More" sheet needs it alongside the overflow items.
const ADMIN_SETTINGS_ITEM = { id: "settings", label: "Settings", desc: "App configuration", icon: <Setting4 size={20} variant="Linear" /> };
const DEALER_PROFILE_ITEM = { id: "dealer-profile", label: "Settings", desc: "Your profile and business details", icon: <Setting4 size={20} variant="Linear" /> };

// How many nav items fit directly in the bottom bar before the rest collapse
// into the "More" sheet (a 5th slot is reserved for the More button itself).
const MOBILE_PRIMARY_COUNT = 4;

// Dealer sections. Listings is the dealer's home: the inventory desk.
const DEALER_NAV_ITEMS = [
  { id: "dealer-dashboard", group: "Work", label: "Dashboard", desc: "Your business at a glance", icon: <Category size={20} variant="Linear" /> },
  { id: "dealer-listings", group: "Work", label: "Listings", desc: "Your inventory and the demand on it", icon: <Home size={20} variant="Linear" /> },
  { id: "dealer-leads", group: "Work", label: "Leads", desc: "Direct and nearby enquiries", icon: <People size={20} variant="Linear" /> },
  { id: "dealer-chat", group: "Work", label: "Inbox", desc: "Chats with tenants and buyers", icon: <Message size={20} variant="Linear" /> },
  { id: "dealer-reels", group: "Work", label: "Reels", desc: "Short videos of your properties", icon: <VideoPlay size={20} variant="Linear" /> },
  { id: "dealer-offices", group: "Team", label: "Offices", desc: "Branch office locations", icon: <Building size={20} variant="Linear" /> },
  { id: "dealer-staff", group: "Team", label: "Staff", desc: "Team members (coming soon)", icon: <Profile size={20} variant="Linear" /> },
];

// Page names for the browser tab title.
const TAB_LABELS = Object.fromEntries(
  [...NAV_ITEMS, ADMIN_SETTINGS_ITEM, ...DEALER_NAV_ITEMS, DEALER_PROFILE_ITEM].map((item) => [item.id, item.label])
);

const groupNav = (items) =>
  items.reduce((groups, item) => {
    const last = groups[groups.length - 1];
    if (last && last.name === item.group) last.items.push(item);
    else groups.push({ name: item.group, items: [item] });
    return groups;
  }, []);

function App() {
  const [activeTab, setActiveTab] = useState("overview");
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null); // 'admin' or 'dealer'
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() =>
    typeof window !== "undefined" && window.innerWidth <= 900
  );
  const { isDark, toggleTheme } = useThemeMode();

  // Auto icon-rail the sidebar on tablet/narrow widths so panels keep usable
  // width; a manual toggle in between resizes still works normally.
  useEffect(() => {
    const applyResponsiveCollapse = () => setSidebarCollapsed(window.innerWidth <= 900);
    window.addEventListener("resize", applyResponsiveCollapse);
    return () => window.removeEventListener("resize", applyResponsiveCollapse);
  }, []);

  // A phone sign-in whose number has no dealer account comes back to the
  // sign-in page with this notice (see the role check below).
  const [loginNotice, setLoginNotice] = useState(null);
  const dismissLoginNotice = useCallback(() => setLoginNotice(null), []);

  // White-label brand (utils/brand.js): live from the dealer's user doc, and
  // cached on this device for the next loading screen.
  const [brandLogoUrl, setBrandLogoUrl] = useState(null);
  const [brandName, setBrandName] = useState("");
  const [brandLogoFailed, setBrandLogoFailed] = useState(false);
  const [bootBrand] = useState(readCachedBrand);

  // A lead opened from a notification, for the Leads panel to bring into view.
  const [leadFocus, setLeadFocus] = useState(null);

  // Mobile bottom-nav "More" sheet: holds nav items that don't fit in the
  // fixed bottom bar (see MOBILE_PRIMARY_COUNT).
  const [showMoreSheet, setShowMoreSheet] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        setUser(null);
        setUserRole(null);
        cacheBrand(null);
        setCheckingAuth(false);
        return;
      }

      // Set by Login just before an OTP sign-in (utils/phoneSignIn.js).
      const phoneSignIn = takePhoneSignIn();
      // A sign-out or another account arriving mid-check supersedes this one.
      const superseded = () => auth.currentUser?.uid !== currentUser.uid;

      try {
        // Check if admin first
        const adminDoc = await getDoc(doc(db, "admins", currentUser.uid));
        if (superseded()) return;
        if (adminDoc.exists()) {
          cacheBrand(null);
          setUserRole("admin");
          setActiveTab("overview");
          setUser(currentUser);
        } else {
          // Check if dealer in users
          const userDocSnap = await getDoc(doc(db, "users", currentUser.uid));
          if (superseded()) return;
          if (userDocSnap.exists() && userDocSnap.data().role === "dealer") {
            const dealerData = userDocSnap.data();
            // Convert legacy locations to offices up front, not only when
            // the Offices tab is opened; Settings and Listings read offices too.
            const offices = await migrateLegacyOffices(currentUser.uid, dealerData).catch((err) => {
              console.error("Office migration failed:", err);
              return dealerData.offices;
            });
            if (superseded()) return;
            setUserRole("dealer");
            setActiveTab("dealer-dashboard");
            setUser({ uid: currentUser.uid, ...dealerData, offices });
          } else if (phoneSignIn) {
            // The dealer check for phone sign-in. It runs here, after the
            // OTP, rather than before one is sent: answering "is this number
            // a dealer?" unauthenticated would let anyone walk the number
            // space and list every dealer. A number without a dealer account
            // is signed straight back out and told why on the sign-in page.
            setLoginNotice({ kind: userDocSnap.exists() ? "not-dealer" : "no-account", phone: phoneSignIn });
            await signOut(auth);
            return;
          } else {
            setUserRole(null);
            setUser(currentUser); // exists but no valid role, will trigger Access Denied page
          }
        }
      } catch (err) {
        console.error("Failed to verify user role:", err);
        if (superseded()) return;
        if (phoneSignIn) {
          setLoginNotice({ kind: "check-failed", phone: phoneSignIn });
          await signOut(auth).catch(() => {});
          return;
        }
        setUserRole(null);
        setUser(currentUser);
      }
      setCheckingAuth(false);
    });
    return () => unsubscribe();
  }, []);

  // The dealer's brand, live: a logo uploaded in Settings (or in another tab)
  // rebrands the rail and the browser tab straight away. Only these two
  // fields are held in state, so the app's presence heartbeat rewriting this
  // doc every minute doesn't re-render the portal.
  useEffect(() => {
    if (!user || userRole !== "dealer") {
      setBrandLogoUrl(null);
      setBrandName("");
      return undefined;
    }
    setBrandLogoUrl(user.brandLogoUrl || null);
    setBrandName(user.name || user.businessName || "");
    const unsubscribe = onSnapshot(doc(db, "users", user.uid), (snap) => {
      const data = snap.data() || {};
      setBrandLogoUrl(data.brandLogoUrl || null);
      setBrandName(data.name || data.businessName || "");
    }, (err) => console.error("Brand listener failed:", err));
    return () => unsubscribe();
  }, [user, userRole]);

  // Total unread chat messages for the rail/bottom-nav "chat" badge.
  // Admins track their unread on support chats via adminUnreadCount; dealers
  // track theirs in the per-participant unreadCount.{uid} map on direct chats.
  const [chatUnreadTotal, setChatUnreadTotal] = useState(0);
  // Support conversations with unread messages, for the admin dashboard.
  const [supportWaiting, setSupportWaiting] = useState(0);
  useEffect(() => {
    if (!user || !userRole) { setChatUnreadTotal(0); setSupportWaiting(0); return; }

    const chatsQuery = userRole === "admin"
      ? query(collection(db, "chats"))
      : query(collection(db, "chats"), where("participants", "array-contains", user.uid));

    const unsubscribe = onSnapshot(chatsQuery, (snap) => {
      let total = 0;
      let waiting = 0;
      snap.forEach((d) => {
        const data = d.data();
        if (userRole === "admin") {
          // Support chats only: direct (customer to dealer) chats have "_" in the id.
          if (!d.id.includes("_")) {
            total += data.adminUnreadCount || 0;
            if ((data.adminUnreadCount || 0) > 0) waiting += 1;
          }
        } else {
          const uc = data.unreadCount;
          total += uc && typeof uc === "object" ? (uc[user.uid] || 0) : 0;
        }
      });
      setChatUnreadTotal(total);
      setSupportWaiting(waiting);
    }, (err) => console.error("Chat unread listener failed:", err));

    return () => unsubscribe();
  }, [user, userRole]);

  // Open abuse-report count for the rail/bottom-nav "reports" badge (admin only).
  const [reportsOpenCount, setReportsOpenCount] = useState(0);
  useEffect(() => {
    if (!user || userRole !== "admin") { setReportsOpenCount(0); return; }
    const q = query(collection(db, "reports"), where("status", "==", "open"));
    const unsubscribe = onSnapshot(q, (snap) => setReportsOpenCount(snap.size), (err) => console.error("Reports count listener failed:", err));
    return () => unsubscribe();
  }, [user, userRole]);

  // Leads still marked New, for the dealer's "Leads" badge.
  const [newLeadsCount, setNewLeadsCount] = useState(0);
  useEffect(() => {
    if (!user || userRole !== "dealer") { setNewLeadsCount(0); return; }
    const q = query(collection(db, "users", user.uid, "leads"), where("status", "==", "New"));
    const unsubscribe = onSnapshot(q, (snap) => setNewLeadsCount(snap.size), (err) => console.error("New leads listener failed:", err));
    return () => unsubscribe();
  }, [user, userRole]);

  // Escape closes the top-most dialog through its own close button, so every
  // panel's modal behaves the same and a disabled close (mid-save) still wins.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      const overlays = document.querySelectorAll(".modal-overlay");
      const top = overlays[overlays.length - 1];
      const close = top && top.querySelector(".modal-header button");
      if (close && !close.disabled) close.click();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    setBrandLogoFailed(false);
  }, [brandLogoUrl]);

  // A logo that fails to load (deleted file) falls back to GharSetu's.
  const isBranded = userRole === "dealer" && !!brandLogoUrl && !brandLogoFailed;
  const brandLogoWide = useWideLogo(isBranded ? brandLogoUrl : null);

  // Remember the brand for this device's next loading screen.
  useEffect(() => {
    if (userRole !== "dealer") return;
    cacheBrand(isBranded ? { logoUrl: brandLogoUrl, name: brandName, wide: brandLogoWide } : null);
  }, [userRole, isBranded, brandLogoUrl, brandName, brandLogoWide]);

  // The browser tab names the page and whose portal it is; a branded portal
  // with a square logo also takes it as the tab icon.
  useEffect(() => {
    if (checkingAuth) return;
    let title = "GharSetu";
    if (!user) {
      title = isTeamSignInPath() ? "Team sign in · GharSetu" : "Dealer sign in · GharSetu";
    } else if (userRole) {
      const owner = userRole === "admin"
        ? "GharSetu admin"
        : isBranded ? brandName || "Dealer portal" : "GharSetu";
      title = `${TAB_LABELS[activeTab] || "Settings"} · ${owner}`;
    }
    applyTabBrand(title, isBranded && !brandLogoWide ? brandLogoUrl : null);
  }, [checkingAuth, user, userRole, activeTab, isBranded, brandLogoUrl, brandName, brandLogoWide]);

  // A lead opened from a notification is brought into view once; leaving
  // Leads drops the request.
  useEffect(() => {
    if (activeTab !== "dealer-leads") setLeadFocus(null);
  }, [activeTab]);

  const handleSignOut = () => {
    signOut(auth).catch((err) => console.error("Sign out failed:", err));
  };

  const openLeadFromNotification = (lead) => {
    setLeadFocus({ ...lead, at: Date.now() });
    setActiveTab("dealer-leads");
  };

  if (checkingAuth) {
    return (
      <div className="gate-screen">
        <div className="gate-loader">
          <img
            src={bootBrand?.logoUrl || "/logo.png"}
            alt=""
            className={`gate-logo${bootBrand ? " is-custom" : ""}${bootBrand?.wide ? " is-wide" : ""}`}
            onError={(e) => {
              if (!e.currentTarget.src.endsWith("/logo.png")) e.currentTarget.src = "/logo.png";
            }}
          />
          <div className="loading-spinner" />
          <p>{bootBrand ? `Loading ${bootBrand.name || "your portal"}` : "Loading GharSetu"}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Login notice={loginNotice} onNoticeDismiss={dismissLoginNotice} />;
  }

  if (!userRole) {
    return (
      <div className="gate-screen">
        <div className="gate-card">
          <img src="/logo.png" alt="GharSetu" className="gate-logo" />
          <h2>No access to this portal</h2>
          <p>This account is not set up as a dealer or an admin. Contact the GharSetu team if you think this is a mistake.</p>
          <button className="btn-primary" onClick={handleSignOut}>Sign out</button>
        </div>
      </div>
    );
  }

  const navItems = userRole === "admin" ? NAV_ITEMS : DEALER_NAV_ITEMS;
  const settingsItem = userRole === "admin" ? ADMIN_SETTINGS_ITEM : DEALER_PROFILE_ITEM;
  const activeNavItem = [...navItems, settingsItem].find((item) => item.id === activeTab);

  // Bottom-nav item split: first N in the bar, the rest (+ settings/profile)
  // behind "More".
  const fullNavItems = [...navItems, settingsItem];
  const mobilePrimaryItems = fullNavItems.slice(0, MOBILE_PRIMARY_COUNT);
  const mobileOverflowItems = fullNavItems.slice(MOBILE_PRIMARY_COUNT);
  const isOverflowActive = mobileOverflowItems.some((item) => item.id === activeTab);

  // Which nav item carries the chat-unread badge, by role.
  const chatNavId = userRole === "admin" ? "chat" : "dealer-chat";
  const formatBadge = (count) => (count > 99 ? "99+" : count);

  // Raw attention count behind a nav item, so counts can be summed before they
  // are formatted for display.
  const navBadgeCountFor = (itemId) => {
    if (itemId === chatNavId) return chatUnreadTotal;
    if (itemId === "reports") return reportsOpenCount;
    if (itemId === "dealer-leads") return newLeadsCount;
    return 0;
  };

  // A nav item's unread/attention badge, if it carries one.
  const navBadgeFor = (itemId) => {
    const count = navBadgeCountFor(itemId);
    return count > 0 ? formatBadge(count) : null;
  };

  // Badged items can sit behind "More" on phones. Roll their counts onto the
  // More button so an unread conversation is still visible without opening it.
  const overflowBadgeTotal = mobileOverflowItems.reduce(
    (sum, item) => sum + navBadgeCountFor(item.id),
    0
  );

  const wideRailLogo = isBranded && brandLogoWide && !sidebarCollapsed;
  const liveName = (userRole === "dealer" && brandName) || user.name;
  const displayName = liveName || user.businessName || user.email || user.contactNumber || "GharSetu";
  const initial = (liveName || user.email || "G").charAt(0).toUpperCase();

  const renderNavButton = (item) => {
    const badge = navBadgeFor(item.id);
    const active = activeTab === item.id;
    return (
      <button
        key={item.id}
        type="button"
        className={`rail-item ${active ? "active" : ""}`}
        onClick={() => setActiveTab(item.id)}
        title={sidebarCollapsed ? item.label : undefined}
        aria-current={active ? "page" : undefined}
        aria-label={badge ? `${item.label}, ${badge} new` : item.label}
      >
        <span className="rail-item-icon">
          {item.icon}
          {badge && sidebarCollapsed && <span className="rail-dot" aria-hidden="true" />}
        </span>
        {!sidebarCollapsed && <span className="rail-item-label">{item.label}</span>}
        {!sidebarCollapsed && badge && (
          <span className={`count-badge ${item.id === chatNavId || item.id === "reports" ? "alert" : ""}`}>{badge}</span>
        )}
      </button>
    );
  };

  return (
    <div className={`app-shell ${sidebarCollapsed ? "rail-collapsed" : ""}`}>
      <aside className="rail" aria-label="Main navigation">
        <div className={`rail-brand${isBranded ? " is-branded" : ""}${wideRailLogo ? " is-wide" : ""}`}>
          <img
            src={isBranded ? brandLogoUrl : "/logo.png"}
            alt=""
            className={`rail-logo${isBranded ? " is-custom" : ""}`}
            onError={isBranded ? () => setBrandLogoFailed(true) : undefined}
          />
          {!sidebarCollapsed && (
            <div className="rail-brand-text">
              {/* A wordmark already shows the name; it stays in the profile row below */}
              <span
                className={wideRailLogo ? "sr-only" : "rail-brand-name"}
                title={isBranded ? brandName : undefined}
              >
                {isBranded ? brandName || "Dealer portal" : "GharSetu"}
              </span>
              <span className={`rail-brand-role${isBranded ? " is-powered" : ""}`}>
                {userRole === "admin" ? "Admin" : isBranded ? "Powered by GharSetu" : "Dealer portal"}
              </span>
            </div>
          )}
          <button
            type="button"
            className="btn-icon rail-collapse"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {sidebarCollapsed ? <ArrowRight2 size={16} variant="Linear" /> : <ArrowLeft2 size={16} variant="Linear" />}
          </button>
        </div>

        <nav className="rail-nav">
          {groupNav(navItems).map((group) => (
            <div className="rail-group" key={group.name}>
              {!sidebarCollapsed && group.name && <span className="rail-group-label">{group.name}</span>}
              {group.items.map(renderNavButton)}
            </div>
          ))}
        </nav>

        <div className="rail-footer">
          <div className="rail-settings-row">
            {renderNavButton(settingsItem)}
            <button
              type="button"
              className="btn-icon rail-theme"
              onClick={toggleTheme}
              aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
              title={isDark ? "Light mode" : "Dark mode"}
            >
              {isDark ? <Sun1 size={18} variant="Linear" /> : <Moon size={18} variant="Linear" />}
            </button>
          </div>

          <div className="rail-profile">
            <span className="avatar" aria-hidden="true">{initial}</span>
            {!sidebarCollapsed && (
              <div className="rail-profile-text">
                <span className="rail-profile-name" title={displayName}>{displayName}</span>
                <span className="rail-profile-role">{userRole === "admin" ? "Super admin" : "Dealer"}</span>
              </div>
            )}
            <button
              type="button"
              className="btn-icon rail-signout"
              onClick={handleSignOut}
              aria-label="Sign out"
              title="Sign out"
            >
              <LogoutCurve size={18} variant="Linear" />
            </button>
          </div>
        </div>
      </aside>

      <div className="app-main">
        <header className="page-header">
          <div className="page-header-text">
            <h1 className="page-title">{activeNavItem?.label || "Settings"}</h1>
            {activeNavItem?.desc && <p className="page-subtitle">{activeNavItem.desc}</p>}
          </div>

          {userRole === "dealer" && (
            <DealerNotifications user={user} onOpenLead={openLeadFromNotification} />
          )}
        </header>

        <main className={`workspace ${activeTab.includes("chat") ? "workspace-chat" : ""}`}>
          {/* Admin Panels */}
          {userRole === "admin" && activeTab === "overview" && (
            <AdminOverview
              setActiveTab={setActiveTab}
              supportUnread={chatUnreadTotal}
              supportWaiting={supportWaiting}
              reportsOpen={reportsOpenCount}
            />
          )}
          {userRole === "admin" && activeTab === "chat" && <ChatPanel />}
          {userRole === "admin" && activeTab === "properties" && <PropertiesPanel />}
          {userRole === "admin" && activeTab === "reels" && <ReelsPanel />}
          {userRole === "admin" && activeTab === "dealers" && <DealersPanel />}
          {userRole === "admin" && activeTab === "users" && <UsersPanel />}
          {userRole === "admin" && activeTab === "notifications" && <NotificationPanel />}
          {userRole === "admin" && activeTab === "reviews" && <ReviewsPanel />}
          {userRole === "admin" && activeTab === "reports" && <ReportsPanel />}
          {userRole === "admin" && activeTab === "banner" && <BannerPanel />}
          {userRole === "admin" && activeTab === "facilities" && <FacilitiesPanel />}
          {userRole === "admin" && activeTab === "settings" && <SettingsPanel />}

          {/* Dealer Panels */}
          {userRole === "dealer" && activeTab === "dealer-dashboard" && (
            <DealerDashboard
              user={user}
              setActiveTab={setActiveTab}
              unreadChats={chatUnreadTotal}
              onOpenLead={openLeadFromNotification}
            />
          )}
          {userRole === "dealer" && activeTab === "dealer-listings" && <DealerListings user={user} setActiveTab={setActiveTab} />}
          {userRole === "dealer" && activeTab === "dealer-reels" && <DealerReels user={user} />}
          {userRole === "dealer" && activeTab === "dealer-leads" && <DealerLeads user={user} setActiveTab={setActiveTab} focusLead={leadFocus} />}
          {userRole === "dealer" && activeTab === "dealer-offices" && <DealerOffices user={user} />}
          {userRole === "dealer" && activeTab === "dealer-chat" && <DealerChat user={user} />}
          {userRole === "dealer" && activeTab === "dealer-staff" && <DealerStaff user={user} />}
          {userRole === "dealer" && activeTab === "dealer-profile" && <DealerProfile user={user} setActiveTab={setActiveTab} />}
        </main>
      </div>

      {/* Mobile bottom navigation, hidden above the phone breakpoint via CSS */}
      <nav className="mobile-bottom-nav" aria-label="Primary">
        {mobilePrimaryItems.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`bottom-nav-item ${activeTab === item.id ? "active" : ""}`}
            onClick={() => { setActiveTab(item.id); setShowMoreSheet(false); }}
            aria-current={activeTab === item.id ? "page" : undefined}
          >
            <span className="bottom-nav-icon">
              {item.icon}
              {navBadgeFor(item.id) && (
                <span className="bottom-nav-badge">{navBadgeFor(item.id)}</span>
              )}
            </span>
            <span className="bottom-nav-label">{item.shortLabel || item.label}</span>
          </button>
        ))}
        {mobileOverflowItems.length > 0 && (
          <button
            type="button"
            className={`bottom-nav-item ${isOverflowActive ? "active" : ""}`}
            onClick={() => setShowMoreSheet((v) => !v)}
            aria-expanded={showMoreSheet}
          >
            <span className="bottom-nav-icon">
              <More size={20} variant="Linear" />
              {overflowBadgeTotal > 0 && (
                <span className="bottom-nav-badge">{formatBadge(overflowBadgeTotal)}</span>
              )}
            </span>
            <span className="bottom-nav-label">More</span>
          </button>
        )}
      </nav>

      {showMoreSheet && (
        <div className="mobile-more-backdrop" onClick={() => setShowMoreSheet(false)}>
          <div className="mobile-more-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-more-handle" />
            {mobileOverflowItems.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`mobile-more-item ${activeTab === item.id ? "active" : ""}`}
                onClick={() => { setActiveTab(item.id); setShowMoreSheet(false); }}
              >
                <span className="rail-item-icon">{item.icon}</span>
                <span>{item.label}</span>
                {navBadgeFor(item.id) && <span className="count-badge alert ml-auto">{navBadgeFor(item.id)}</span>}
              </button>
            ))}
            <button type="button" className="mobile-more-item" onClick={toggleTheme}>
              <span className="rail-item-icon">{isDark ? <Sun1 size={20} variant="Linear" /> : <Moon size={20} variant="Linear" />}</span>
              <span>{isDark ? "Light mode" : "Dark mode"}</span>
            </button>
            <button
              type="button"
              className="mobile-more-item mobile-more-signout"
              onClick={() => { setShowMoreSheet(false); handleSignOut(); }}
            >
              <span className="rail-item-icon"><LogoutCurve size={20} variant="Linear" /></span>
              <span>Sign out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
